import { HttpClient, httpResource } from '@angular/common/http';
import { computed, effect, inject, signal, untracked, type Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable, of, tap } from 'rxjs';
import { type ListResponse } from './api-resource/resource.types';
import { fetchAllPages } from './api-resource/fetch-all-pages';
import { CatalogInvalidationService } from './catalog-invalidation.service';
import { SearchService } from './search.service';
import { LanguageService } from './language.service';
import { USER_ID_PROVIDER } from './user-scoped-storage.service';
import { FIELD_PERMISSION_CHECKER } from '../entity-edit/field-permission-checker';

export interface ResourceState<T> {
  /** The items array from the paginated response (all accumulated pages) */
  value: Signal<T[]>;
  /** Total count of all items from the API (before any host-side filtering) */
  totalCount: Signal<number | undefined>;
  /** Whether the first page is currently loading */
  isLoading: () => boolean;
  /** Whether a subsequent page is currently being fetched via loadNextPage() */
  isLoadingMore: () => boolean;
  /** Any error from the last request */
  error: () => Error | undefined;
}

export abstract class BaseResourceService<T extends { id: string }> {
  protected readonly http = inject(HttpClient);
  protected readonly catalogInvalidation = inject(CatalogInvalidationService);
  protected readonly searchService = inject(SearchService);
  private readonly languageService = inject(LanguageService);
  private readonly userIdFn = inject(USER_ID_PROVIDER, { optional: true });
  private readonly currentUserId = computed(() => this.userIdFn?.() ?? '__anon__');
  private readonly hasUserContext = computed(() => !this.userIdFn || this.userIdFn() !== undefined);
  // Optional at root scope so tests / early bootstrap without the app-level
  // provider still resolve. When absent, no permission gating is applied.
  private readonly permissions = inject(FIELD_PERMISSION_CHECKER, { optional: true });

  declare protected abstract readonly basePath: string;

  readonly selectedId = signal<string | undefined>(undefined);

  readonly version = computed(() => this.catalogInvalidation.version(this.basePath)());

  protected readonly searchParamName: string | null = 'name';

  /**
   * Read permission that gates the auto-loading list resource. When set, the
   * initial fetch is suppressed for users without this permission — the
   * service is typically `providedIn: 'root'` and instantiation triggers the
   * `rxResource` fetch, which would otherwise 403 for restricted callers.
   *
   * Explicit APIs (`create`, `selectByIdOnce`, `loadByTeamOnce`, ...) remain
   * unaffected — callers that opt in are assumed to have the permission.
   */
  protected readonly readPermission: string | null = null;

  private static readonly PAGE_SIZE = 50;

  /** Accumulated extra pages fetched after the first via loadNextPage(). */
  private readonly _accState = signal<{
    paramsKey: string;
    items: T[];
    hasMore: boolean;
    nextSkip: number;
  } | null>(null);

  private readonly _isLoadingMore = signal(false);

  // Value-stable gate: isolates permissionSet identity churn (30s poll mints a
  // new array) so params below only invalidates on real boolean changes.
  private readonly canAutoLoadListFlag = computed(() => this.canAutoLoadList());

  /** Encodes all reactive dependencies so stale acc state can be detected. */
  private readonly _paramsKey = computed(() => {
    const userId = this.currentUserId();
    const version = this.version();
    const lang = this.languageService.currentLanguage();
    const url = this.buildListUrl();
    return `${userId}|${version}|${lang}|${url ?? ''}`;
  });

  private readonly listResource = rxResource<ListResponse<T>, { userId: string; url: string | undefined; version: number; lang: string }>({
    params: () => ({
      userId: this.currentUserId(),
      lang: this.languageService.currentLanguage(),
      version: this.version(),
      url: this.hasUserContext() && this.canAutoLoadListFlag() ? this.buildListUrl() : undefined,
    }),
    stream: ({ params: { url } }) => (url ? this.fetchPageFromUrl(url, 0) : of(this.emptyListResponse())),
    defaultValue: this.emptyListResponse(),
  });

  private canAutoLoadList(): boolean {
    if (!this.readPermission) return true;
    if (!this.permissions) return true;
    return this.permissions.hasPermission(this.readPermission);
  }

  protected readonly itemSuffix: string = '';

  readonly selected = httpResource<T>(() => {
    const id = this.selectedId();
    if (!id || !this.hasUserContext()) return undefined;
    return `${this.basePath}/${id}${this.itemSuffix}`;
  });

  /** Whether there are more pages available from the API (beyond what is already accumulated). */
  readonly hasMoreApiPages = computed(() => {
    const key = this._paramsKey();
    const acc = this._accState();
    if (acc?.paramsKey === key) return acc.hasMore;
    return !this.listResource.isLoading() && (this.listResource.value()?.meta.hasMore ?? false);
  });

  readonly items: ResourceState<T> = {
    value: computed(() => {
      const firstPage = this.listResource.value()?.data ?? [];
      const key = this._paramsKey();
      const acc = this._accState();
      return acc?.paramsKey === key ? [...firstPage, ...acc.items] : firstPage;
    }),
    totalCount: computed(() => this.listResource.value()?.meta.total),
    isLoading: () => this.listResource.isLoading(),
    isLoadingMore: () => this._isLoadingMore(),
    error: () => this.listResource.error() as Error | undefined,
  };

  constructor() {
    effect(() => {
      this.currentUserId();
      untracked(() => {
        this._accState.set(null);
        this.selectedId.set(undefined);
      });
    });
    // Reset accumulated pages whenever a fresh first-page load starts.
    effect(() => {
      if (this.listResource.isLoading()) {
        untracked(() => this._accState.set(null));
      }
    });
  }

  /** Fetch the next page from the API and append it to the accumulated items. */
  loadNextPage(): void {
    if (this._isLoadingMore() || this.listResource.isLoading() || !this.hasMoreApiPages()) return;
    const key = this._paramsKey();
    const acc = this._accState();
    const firstMeta = this.listResource.value()?.meta;
    const nextSkip = acc?.paramsKey === key ? acc.nextSkip : (firstMeta?.skip ?? 0) + (firstMeta?.count ?? 0);
    const url = this.buildListUrl();
    if (!url) return;
    this._isLoadingMore.set(true);
    this.fetchPageFromUrl(url, nextSkip).subscribe({
      next: response => {
        const currentKey = this._paramsKey();
        if (currentKey !== key) {
          this._isLoadingMore.set(false);
          return;
        }
        const prevItems = this._accState()?.paramsKey === key ? (this._accState()?.items ?? []) : [];
        this._accState.set({
          paramsKey: key,
          items: [...prevItems, ...response.data],
          hasMore: response.meta.hasMore,
          nextSkip: response.meta.skip + response.meta.count,
        });
        this._isLoadingMore.set(false);
      },
      error: () => this._isLoadingMore.set(false),
    });
  }

  protected buildListUrl(): string | undefined {
    const globalSearch = this.searchService.catalogSearchQuery();
    if (globalSearch && this.searchParamName) {
      const params = new URLSearchParams();
      params.set(this.searchParamName, globalSearch);
      return `${this.basePath}?${params.toString()}`;
    }
    return this.basePath;
  }

  private fetchPageFromUrl(url: string, skip: number): Observable<ListResponse<T>> {
    const qIdx = url.indexOf('?');
    const path = qIdx >= 0 ? url.slice(0, qIdx) : url;
    const qs = qIdx >= 0 ? url.slice(qIdx + 1) : '';
    const params = new URLSearchParams(qs);
    params.set('skip', String(skip));
    params.set('take', String(BaseResourceService.PAGE_SIZE));
    return this.http.get<ListResponse<T>>(`${path}?${params.toString()}`);
  }

  private emptyListResponse(): ListResponse<T> {
    return { data: [], meta: { take: 0, skip: 0, count: 0, total: 0, hasMore: false } };
  }

  reload(): void {
    this.catalogInvalidation.invalidate(this.basePath);
  }

  selectById(id: string): void {
    this.selectedId.set(id);
  }

  clearSelection(): void {
    this.selectedId.set(undefined);
  }

  create<TCreate>(data: TCreate) {
    return this.http.post<T>(this.basePath, data).pipe(tap(() => this.reload()));
  }

  update<TUpdate>(id: string, data: TUpdate) {
    return this.http.patch<T>(`${this.basePath}/${id}`, data).pipe(tap(() => this.reload()));
  }

  delete(id: string) {
    return this.http.delete<void>(`${this.basePath}/${id}`).pipe(tap(() => this.reload()));
  }

  loadAllOnce(options?: { sort?: string }): Observable<T[]> {
    return fetchAllPages<T>(this.http, {
      basePath: this.basePath,
      sort: options?.sort,
    });
  }

  selectByIdOnce(id: string): Observable<T> {
    return this.http.get<T>(`${this.basePath}/${id}`);
  }
}
